const API_BASE = '/api/v1';

class ApiClient {
  private accessToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken() {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401 && this.accessToken) {
      // Try to refresh the token
      const refreshed = await this.refresh();
      if (refreshed) {
        // Retry the request with new token
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include',
        });

        if (!retryResponse.ok) {
          const error = await retryResponse.json();
          throw new Error(error.error?.message || 'Request failed');
        }

        return retryResponse.json();
      }
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Request failed');
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  private async refresh(): Promise<boolean> {
    // Prevent multiple concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          this.accessToken = data.accessToken;
          return true;
        }

        this.accessToken = null;
        return false;
      } catch {
        this.accessToken = null;
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // Auth
  async register(email: string, password: string) {
    const data = await this.request<{
      accessToken: string;
      expiresIn: number;
      user: User;
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.accessToken = data.accessToken;
    return data;
  }

  async login(email: string, password: string) {
    const data = await this.request<{
      accessToken: string;
      expiresIn: number;
      user: User;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.accessToken = data.accessToken;
    return data;
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' });
    this.accessToken = null;
  }

  async refreshToken() {
    const data = await this.request<{
      accessToken: string;
      expiresIn: number;
      user: User;
    }>('/auth/refresh', { method: 'POST' });
    this.accessToken = data.accessToken;
    return data;
  }

  async getAuthMethods(email: string) {
    return this.request<{
      password: boolean;
      passkey: boolean;
      magicLink: boolean;
    }>(`/auth/methods?email=${encodeURIComponent(email)}`);
  }

  async requestMagicLink(email: string) {
    return this.request<{ success: boolean; message: string }>(
      '/auth/magic-link/request',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async verifyMagicLink(token: string) {
    const data = await this.request<{
      accessToken: string;
      expiresIn: number;
      user: User;
    }>('/auth/magic-link/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    this.accessToken = data.accessToken;
    return data;
  }

  // Passkeys
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getPasskeyRegistrationOptions(name?: string): Promise<any> {
    return this.request('/auth/passkey/register/options', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async verifyPasskeyRegistration(response: unknown, name?: string) {
    return this.request<{ id: string; name: string; createdAt: string }>(
      '/auth/passkey/register/verify',
      {
        method: 'POST',
        body: JSON.stringify({ response, name }),
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getPasskeyLoginOptions(email: string): Promise<any> {
    return this.request(
      `/auth/passkey/login/options?email=${encodeURIComponent(email)}`
    );
  }

  async verifyPasskeyLogin(email: string, response: unknown) {
    const data = await this.request<{
      accessToken: string;
      expiresIn: number;
      user: User;
    }>('/auth/passkey/login/verify', {
      method: 'POST',
      body: JSON.stringify({ email, response }),
    });
    this.accessToken = data.accessToken;
    return data;
  }

  async listPasskeys() {
    return this.request<{
      passkeys: Passkey[];
    }>('/auth/passkeys');
  }

  async deletePasskey(id: string) {
    return this.request<{ success: boolean }>(`/auth/passkeys/${id}`, {
      method: 'DELETE',
    });
  }

  // Entries
  async listEntries(params: Record<string, string> = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request<PaginatedResponse<Entry>>(
      `/entries${query ? `?${query}` : ''}`
    );
  }

  async getEntry(id: string) {
    return this.request<Entry>(`/entries/${id}`);
  }

  async createEntry(data: CreateEntryInput) {
    return this.request<Entry>('/entries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEntry(id: string, data: UpdateEntryInput) {
    return this.request<Entry>(`/entries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEntry(id: string) {
    return this.request<void>(`/entries/${id}`, { method: 'DELETE' });
  }

  async getTags() {
    return this.request<{ tags: string[] }>('/entries/tags');
  }

  async listEntryDates(params: Record<string, string> = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request<{ dates: string[] }>(
      `/entries/dates${query ? `?${query}` : ''}`
    );
  }

  async getEntryLocations(params: Record<string, string> = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request<{ pins: EntryLocationPin[] }>(
      `/entries/locations${query ? `?${query}` : ''}`
    );
  }

  async getPreviousEntryId(id: string) {
    return this.request<{ id: string | null }>(`/entries/${id}/previous`);
  }

  async getNextEntryId(id: string) {
    return this.request<{ id: string | null }>(`/entries/${id}/next`);
  }

  async getEntriesOnThisDay() {
    return this.request<{ entries: Entry[] }>('/entries/on-this-day');
  }

  async getWordCountStats(days = 30) {
    return this.request<{ stats: { date: string; wordCount: number }[] }>(
      `/entries/stats?days=${days}`
    );
  }

  async uploadImage(entryId: string, file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${API_BASE}/entries/${entryId}/images`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to upload image');
    }
    return response.json();
  }

  async deleteImage(entryId: string, imageId: string): Promise<void> {
    return this.request(`/entries/${entryId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  async fetchImageBlob(url: string): Promise<Blob> {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch image');
    return response.blob();
  }

  async reverseGeocode(lat: number, lon: number): Promise<{
    name?: string;
    display_name?: string;
    address?: Record<string, string>;
  }> {
    return this.request<{
      name?: string;
      display_name?: string;
      address?: Record<string, string>;
    }>(`/geocode/reverse?lat=${lat}&lon=${lon}`);
  }

  // Export/Import
  async exportJson(params: Record<string, string> = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_BASE}/export/json${query ? `?${query}` : ''}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
        credentials: 'include',
      }
    );
    return response.blob();
  }

  async exportPdf(params: Record<string, string> = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_BASE}/export/pdf${query ? `?${query}` : ''}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
        credentials: 'include',
      }
    );
    return response.blob();
  }

  async importJson(data: unknown) {
    return this.request<{
      imported: number;
      skipped: number;
      errors: { index: number; error: string }[];
    }>('/import/json', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Settings
  async getOnboardingStatus() {
    return this.request<OnboardingStatus>('/settings/onboarding/status');
  }

  async completeOnboarding(data: { instanceName?: string }) {
    return this.request<{ success: boolean }>('/settings/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPreferences() {
    return this.request<{
      preferences: UserPreferences;
      email: string;
      authMethods: string[];
    }>('/settings/preferences');
  }

  async updatePreferences(data: Partial<UserPreferences>) {
    return this.request<{ preferences: UserPreferences }>(
      '/settings/preferences',
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  async getSmtpSettings() {
    return this.request<SmtpSettingsResponse>('/settings/smtp');
  }

  async updateSmtpSettings(config: SmtpConfig) {
    return this.request<{ success: boolean }>('/settings/smtp', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteSmtpSettings() {
    return this.request<{ success: boolean }>('/settings/smtp', {
      method: 'DELETE',
    });
  }

  async verifySmtp(config: SmtpConfig) {
    return this.request<{ success: boolean }>('/settings/smtp/verify', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async sendTestEmail(config: SmtpConfig & { recipientEmail: string }) {
    return this.request<{ success: boolean }>('/settings/smtp/test', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async getAppInfo() {
    return this.request<{ instanceName: string; version: string }>(
      '/settings/info'
    );
  }
}

// Types
interface User {
  id: string;
  email: string;
  preferences: UserPreferences;
  authMethods: string[];
}

interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  language: 'en' | 'pl';
  editorFontSize: number;
}

interface Passkey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
  deviceType: string;
  backedUp: boolean;
}

interface Entry {
  id: string;
  title: string;
  content: string;
  tags: string[];
  entryDate: string;
  updatedAt: string;
  deletedAt?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationName?: string;
  wordCount?: number;
}

interface CreateEntryInput {
  title: string;
  content: string;
  tags?: string[];
  locationLatitude?: number;
  locationLongitude?: number;
  locationName?: string;
}

interface UpdateEntryInput {
  title?: string;
  content?: string;
  tags?: string[];
  entryDate?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationName?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  fromAddress: string;
  fromName?: string;
}

interface SmtpSettingsResponse {
  configured: boolean;
  config: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    fromAddress: string;
    fromName: string;
  } | null;
}

interface OnboardingStatus {
  isComplete: boolean;
  currentStep: number;
  hasUser: boolean;
  hasSmtp: boolean;
}

interface EntryLocationPin {
  id: string;
  locationLatitude: number;
  locationLongitude: number;
  locationName?: string;
  preview: string;
}

interface ImageUploadResponse {
  id: string;
  entryId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  markdown: string;
  createdAt: string;
}

export const api = new ApiClient();
export type {
  User,
  UserPreferences,
  Passkey,
  Entry,
  EntryLocationPin,
  CreateEntryInput,
  UpdateEntryInput,
  PaginatedResponse,
  SmtpConfig,
  SmtpSettingsResponse,
  OnboardingStatus,
  ImageUploadResponse,
};
